import { NllLockSubject, NllLockType } from "@/store/modules/recept/helpers";

/*
Exempel på resultat vid hämtning av läkemedel (2025-11-04)
{
    "nplPackId": "20191025100241",
    "nplId": "20040922000065",
    "drugId": "20040922000065",
    "distributedTradeName": "Abilify",
    "distributedTradeNameLegal": "Abilify",
    "hospitalUseOnlyFlag": false,
    "packSizeNumeric": "28",
    "packSizeNumericUnit": "tablett(er)",
    "packSizeText": "28 tablett(er)",
    "packageText": "Blister, 28 x 1 tabletter (PD: Medartuum AB)",
    "limitedReimbursement": null,
    "otcFlag": false,
    "marketedFlag": true,
    "withdrawalDate": null,
    "isRecommended": false,
    "isAntiRecommended": false,
    "interchangeableFlag": true,
    "isReimbursed": true,
    "salesStoppedFlag": false,
    "aup": "1189.79",
    "hasShortage": false,
    "forecastFromDate": null,
    "forecastThroughDate": null,
    "parallelDistributor": "Medartuum AB",
    "intressent": "Medartuum AB",
    "atcCode": "N05AX12",
    "tradeName": "Abilify",
    "tradeNameLegal": "Abilify",
    "strengthNumeric": "15",
    "strengthText": "15 mg",
    "substanceDescription": "Aripiprazol",
    "drugFormTextSv": "Munsönderfallande tablett",
    "additionalMonitoringFlag": false,
    "controlledDrugFlag": false,
    "containsLactosFlag": true,
    "containsLatexFlag": false,
    "drugFormCode": "ORDITA",
    "dispensingRestrictionCode": "-",
    "rxRepeatableCode": "-",
    "marketingAuthHolder": "Otsuka Pharmaceutical Netherlands B.V.",
    "localRepresentative": "Otsuka Pharma Scandinavia AB",
    "registrationHolder": null,
    "legalManufacturer": null,
    "linkFASS": "https://www.fass.se/LIF/product?userType=0&nplId=20040922000065&docType=3",
    "productTypeCode": "MEP",
    "controlClassCode": "0",
    "controlClass": 0,
    "controlClassText": "Ej narkotikaklassad"
}
*/
export type Lakemedel = {
    /**
     * Sätts vid hämtning i store
     */
    id: Lakemedel["nplPackId"];
    nplPackId: string;
    nplId: string;
    drugId: string;
    distributedTradeName: string;
    distributedTradeNameLegal: string;
    hospitalUseOnlyFlag: boolean;
    packSizeNumeric: number;
    packSizeText: string;
    packageText: string;
    limitedReimbursement?: string;
    otcFlag: boolean;
    marketedFlag: boolean;
    withdrawalDate: unknown;
    isRecommended: boolean;
    isAntiRecommended: boolean;
    interchangeableFlag: boolean;
    isReimbursed: boolean;
    salesStoppedFlag: boolean;
    aup: number;
    hasShortage: boolean;
    forecastFromDate: unknown;
    forecastThroughDate: unknown;
    parallelDistributor: string | null;
    intressent: string | null;
    atcCode: string;
    tradeName: string;
    tradeNameLegal: string;
    strengthNumeric: number;
    strengthText: string;
    substanceDescription: string;
    drugFormTextSv: string;
    additionalMonitoringFlag: boolean;
    controlledDrugFlag: boolean;
    containsLactosFlag: boolean;
    containsLatexFlag: boolean;
    drugFormCode: string;
    dispensingRestrictionCode: string;
    rxRepeatableCode: string;
    marketingAuthHolder: string | null;
    localRepresentative: string | null;
    registrationHolder: string | null;
    legalManufacturer: string | null;
    linkFASS: string;
    productTypeCode: string;
    controlClassCode: number;
    controlClass: number;
    controlClassText: string;
    productDescription: string | null;
};

export type LakemedelInfo = {
    namn: Lakemedel["distributedTradeName"];
    tillverkare: Lakemedel["intressent"];
    styrka: Lakemedel["strengthText"];
    form: Lakemedel["drugFormTextSv"];
    forpackningsinnehall: Lakemedel["packageText"];
    substans: Lakemedel["substanceDescription"];
    atc: Lakemedel["atcCode"];
};

// TODO: Följ mönstret för handelsvaror och referera till motsvarande fält i Lakemedel
export type LakemedelDisplay = {
    namn: string;
    tillverkare: string | null;
    /**
     * Samma värde som nplPackId. Används som unikt värde för att identifiera enskilda läkemedel i store
     */
    id: LakemedelDisplay["nplPackId"];
    forpackning: string;
    linkFass: string;
    substans: string;
    styrka: string;
    atc: string;
    form: string;
    lakemedel: LakemedelInfo;
    kontrollerat: boolean;
    rekommenderat: boolean;
    restnoterat: boolean;
    formansrestriktion?: string;
    /**
     * Skickas till BE för hämtning av mer data för läkemedel, oftast i kombination med nplPackId
     */
    nplId: string;
    nplPackId: string;
    inomForman: boolean;
    narkotikaNiva: number;
    mangd: number;
};

export type Handelsvara = {
    articleName: string;
    articleNo: string;
    assortmentCode: string;
    companySupplierName: string;
    containsLatexFlag: boolean;
    isPrescriptionRequired: boolean;
    isReimbursed: boolean;
    limitedReimbursement: boolean | null;
    packDescription: string | null;
    productDescription: string | null;
    sizeText: string;
    strengthText: string | null;
    sizeNum: number;
};

type HandelsvaraInfo = {
    namn: Handelsvara["articleName"];
    tillverkare: Handelsvara["companySupplierName"];
    varunr: Handelsvara["articleNo"];
    benamning: Handelsvara["productDescription"];
    storlek: Handelsvara["sizeText"];
};

export type HandelsvaraDisplay = {
    id: Handelsvara["articleNo"];
    styrka: Handelsvara["strengthText"];
    benamning: Handelsvara["productDescription"];
    // TODO: Bör byta namn till artikelInfo i både LakemedelDisplay och HandelsvaraDisplay, alt. handelsvaraInfo här
    lakemedelInfo: HandelsvaraInfo;
    formansrestriktion: Handelsvara["limitedReimbursement"];
    inomForman: Handelsvara["isReimbursed"];
    forpackning: Handelsvara["packDescription"];
    mangd: Handelsvara["sizeNum"];
};

export type ArtikelInfo = LakemedelInfo | HandelsvaraInfo;

export type NLLPatient = {
    kliniken_id: string;
    nll_id?: string;
    personId: string;
    personIdType?: string;
    fornamn: string;
    efternamn: string;
    tilltalsnamn_index: number;
    avliden: boolean;
    sekretess: boolean;
    skyddad_identitet: boolean;
    folkbokford: string;
    lan: number;
    kommun: number;
    skillnader?: string | null;
};

// Basinformation om patient för när det inte går att hämta den från NLL
export type BasePatient = {
    kliniken_id: string;
    personId: string;
    personIdType?: string;
    fornamn: string;
    efternamn: string;
    dateOfBirth: string;
};

export type BasePatientInfo = {
    patient: BasePatient;
    formansberattigad: boolean;
    loading?: boolean;
};

export type NLLPatientInfo = {
    patient: NLLPatient;
    samtycke: string | null;
    version: number;
    sarskilda_lakemedel_finns: boolean;
    sparrar_finns: boolean; // kan bara ses med samtycke
    dospatient: boolean;
    formansberattigad: boolean;
    samtyckesforfragan_finns: string | null;
    loading?: boolean;
    sparr_pappersrecept_vardnadshavare: string | null;
};

export const isNLLPatientInfo = (info: NLLPatientInfo | BasePatientInfo): info is NLLPatientInfo => {
    const typeAttrs = [
        "patient",
        "samtycke",
        "version",
        "sarskilda_lakemedel_finns",
        "sparrar_finns",
        "dospatient",
        "formansberattigad",
    ];
    return info && typeAttrs.every((attr) => attr in info);
};

export type NLLConsentType = {
    code: string;
    display: string;
    registered: boolean;
    summary?: string;
    information?: string;
    additionalInfo?: string;
    assurance?: string;
    minAge?: number;
    maxAge?: number;
};

export const NllSamtycke = {
    DospatientsUppgifter: "DOSPATIENTS_UPPGIFTER",
    PrivatTillVardpersonal: "SAMTYCKE_PRIVAT_TILL_VARDPERSONAL",
    Tillfalligt: "TILLFALLIGT_SAMTYCKE",
    Nodatkomst: "NODATKOMST",
    Formodat: "FORMODAT_SAMTYCKE",
    UtanKravBarn: "UTAN_KRAV_SAMTYCKE_BARN",
    UtanKrav: "UTAN_KRAV_SAMTYCKE",
    TillfalligtSparradeUppgifter: "TILLFALLIGT_SAMTYCKE_SPARRADE_UPPGIFTER",
};

export const NLL_CONSENT_TYPES = new Map<string, NLLConsentType>([
    [
        NllSamtycke.DospatientsUppgifter,
        {
            code: NllSamtycke.DospatientsUppgifter,
            display: "Åtkomst till dospatients uppgifter",
            registered: true,
        },
    ],
    [
        NllSamtycke.PrivatTillVardpersonal,
        {
            code: NllSamtycke.PrivatTillVardpersonal,
            display: "Registrerat åtkomstsamtycke",
            registered: true,
        },
    ],
    [
        NllSamtycke.Tillfalligt,
        {
            code: NllSamtycke.Tillfalligt,
            display: "Tillfälligt samtycke", // Alternativt "Tillfälligt samtycke till åtkomst"?
            summary: "Ett tillfälligt samtycke gäller för en vårdkontakt om patienten ger sitt samtycke",
            information:
                "Ett tillfälligt samtycke gäller för en vårdkontakt och för den hälso- och sjukvårdspersonal som uppfyller något av ändamålen under vårdkontakten. Ett samtycke kan inhämtas i förväg eller i samband med vårdkontakten. Väljer du denna åtkomsttyp intygar du att ett samtycke har inhämtats av patienten.",
            assurance: "Jag intygar att patienten gett sitt samtycke.",
            registered: false,
        },
    ],
    [
        NllSamtycke.Formodat,
        {
            code: NllSamtycke.Formodat,
            display: "Förmodat samtycke",
            summary:
                "Ange förmodat samtycke om patienten är oförmögen att ge samtycke och förmodas inte motsätta sig åtkomst",
            information:
                "Hälso- och sjukvårdspersonal kan hänvisa till ett förmodat samtycke för att vid en vårdkontakt tillfälligt få tillgång till en patients läkemedelslista under förutsättning att patientens inställning så långt som möjligt har klarlagts och det inte finns anledning att tro att patienten skulle ha motsatt sig åtkomst. Dessa förutsättningar ska framgå av patientens journal. Väljer du ett förmodat samtycke intygar du att förutsättningarna för detta finns.",
            assurance: "Jag intygar att villkoren för förmodat samtycke är uppfyllda.",
            registered: false,
            minAge: 18,
        },
    ],
    [
        NllSamtycke.UtanKravBarn,
        {
            code: NllSamtycke.UtanKravBarn,
            display: "Åtkomst till barns uppgifter", // Alternativt "Utan krav på samtycke barn"?
            summary:
                "Vårdpersonal kan få tillgång till barns uppgifter i NLL utan krav på samtycke om uppgifterna har betydelse för barnets vård",
            information:
                "Hälso- och sjukvårdspersonal kan få tillgång till barns uppgifter i Nationella läkemedelslistan utan krav på samtycke under förutsättning att uppgifterna har betydelse för den vård som ges till barnet och barnet inte har nått tillräcklig mognad för att förstå vad ett samtycke innebär. Väljer du åtkomst till barns uppgifter utan krav på samtycke intygar du samtidigt att förutsättningarna för detta finns.",
            additionalInfo:
                "Fördjupad information:\nVårdnadshavare har inte rätt att spärra information om sitt barn gentemot vårdens eller apotekens ändamål för direktåtkomst. Det medför att vårdnadshavare inte kan ge samtycke i sitt barns ställe för direktåtkomst till barnets uppgifter.\nEn lämplig ålder att fråga om samtycke för direktåtkomst till ett barns läkemedelslista är från 15 år, under förutsättning att barnet bedöms ha nått tillräcklig mognadsgrad för att förstå innebörden av samtycket. Det hindrar dock inte att samtycke kan inhämtas tidigare, om barnet bedöms ha nått tillräcklig mognad och utveckling för att förstå vad samtycket innebär.\nHälso- och sjukvårdspersonal kan få tillgång till barns uppgifter i Nationella läkemedelslistan utan krav på samtycke, om de bedömer att uppgifterna har betydelse för den vård som ges till barnet och barnet inte har nått tillräcklig mognad för att förstå vad ett samtycke innebär.",
            assurance:
                "Jag intygar att villkoren för begäran av åtkomst till barns uppgifter är uppfyllda. Alternativt är barnet för litet för att kunna ge samtycke, då behöver inget val av åtkomsttyp intygas.",
            registered: false,
            maxAge: 17,
        },
    ],
    [
        NllSamtycke.Nodatkomst,
        {
            code: NllSamtycke.Nodatkomst,
            display: "Nödåtkomst",
            summary: "I nödsituationer kan vårdpersonal begära nödåtkomst till en patients läkemedelslista",
            information:
                "I nödsituationer kan hälso- och sjukvårdspersonal begära nödåtkomst till en patients läkemedelslista. Detta under förutsättning att patienten är oförmögen att lämna samtycke och information om patientens läkemedelsbehandling i Nationella läkemedelslistan är nödvändig för den vård eller behandling som patienten oundgängligen behöver samt att inhämtande av samtycke inte kan inväntas.\nGrunden för denna bedömning ska framgå av patientens journal. Väljer du nödåtkomst intygar du att förutsättningarna för detta finns.",
            assurance: "Jag intygar att villkoren för nödåtkomst är uppfyllda.",
            registered: false,
        },
    ],
    [
        NllSamtycke.TillfalligtSparradeUppgifter,
        {
            code: NllSamtycke.TillfalligtSparradeUppgifter,
            display: "Samtycke till tillfällig åtkomst till spärrade uppgifter", // Rekommenderad benämning "Samtycke se spärrade uppgifter"
            registered: false,
        },
    ],
]);

export const hasRegisteredConsent = (consent: string | null) => {
    if (consent) {
        return NLL_CONSENT_TYPES?.get(consent)?.registered;
    }
    return false;
};

export const AvslutaMakulera = {
    avsluta: "AVSLUTA_FORSKRIVNING",
    makulera: "MAKULERA_FORSKRIVNING",
};

export type Telefonnummer = {
    value: string;
    available_to_patient: boolean;
};

export type Vardenhet = {
    namn: string;
    adress: string[];
    telefonnummer: Telefonnummer[];
};

export type Forskrivare = {
    fornamn: string;
    efternamn: string;
    roll: string;
    telefonnummer: Telefonnummer[];
    fullstandigt_namn?: string;
    landskod?: string;
    vardenhet: Vardenhet;
};

export const Forskrivningsstatus = {
    Aktiv: "Aktiv",
    Parkerad: "Parkerad",
    Slutexpedierad: "Slutexpedierad",
    Makulerad: "Makulerad",
    Avslutad: "Avslutad",
    Expedierat: "Expedierat", // För uttag pappersrecept
    Borttaget: "Borttaget", // För uttag pappersrecept
};

// TODO: Verkar inte stämma helt med formen på uttag?
export type ReceptforskrivningUttag = {
    id: string;
    expeditionstidpunkt: string;
    formansval: string | null;
    formansval_code: string | null;
    uttagsnotering: string | null;
    doseringsanvisning: string | null;
    uttagna_forpackningar: number;
    uttagen_mangd: number;
    forpackningsenhet: string;
    handelsvara: Handelsvara | null;
    lakemedel: Lakemedel | null;
    substitution_utford: boolean | null;
    uttagstyp: string | null;
    krediteringstidpunkt: string | null;
    kreditering_expeditionsid: string | null;
};

export type Sparr = {
    id: string;
    target_type: string;
    target_id: string;
    prescription_chain_id: string;
    legal_ground: keyof typeof NllLockType;
    subject_type: keyof typeof NllLockSubject;
    status: string;
};

export type Handelse = {
    beskrivning: string | null;
    handelse: string;
    handelse_typ: "REGISTRERA_FORSKRIVNING";
    id: string;
    orsak: string | null;
    orsakskod: string | null;
    tidpunkt: string;
    uppdragsgivare: Forskrivare | null;
    utforare: Forskrivare;
    info?: string;
};

type ReceptforskrivningBase = {
    id: string;
    version_id: string;
    forskrivningskedja_id: string | null;
    forskrivare: Forskrivare;
    forskrivningsstatus: keyof typeof Forskrivningsstatus;
    forskrivningsstatusorsak: string | null;
    utfardandedatum: string;
    sista_giltighetsdag: string;
    typ_av_registreringsunderlag: "Elektroniskt" | "Papper" | "Telefon";
    aktuellt_format: "Elektroniskt" | "Papper";
    varningar: any[];
    formansberattigad_patient: boolean;
    formansvillkor_for_lakemedel_uppfyllda: boolean | null;
    forskrivning_enligt_smittskyddslagen: boolean;
    forskrivarens_kommentar: string | null;
    apotekets_kommentar: string | null;
    notat_forskrivare: string | null;
    leveransmeddelande: string | null;
    beskrivning_artikel: string | null;
    behandlingsorsak: string;
    annan_orsak: string | null;
    senaste_datum_for_uppfoljning_av_behandling: string | null;
    senaste_datum_for_avslut_av_behandling: string | null;
    doseringsanvisning: string;
    substitution_tillaten: boolean;
    antal_uttag: number;
    forpackningar_per_uttag: number;
    kvarvarande_mangd: string;
    forsta_doseringsdag: string | null;
    sista_doseringsdag: string | null;
    startforpackning: boolean;
    forsta_uttag_fore: string | null;
    nasta_uttag_inom_forman: string | null;
    tid_mellan_uttag: string | null;
    antal_uttag_kvar: number;
    forskriven_mangd_per_uttag?: string;
    vid_behov: boolean;
    obs_overstiger_maxdosering: boolean;
    administreringsmetod: string | null;
    administreringsvag: string | null;
    administreringsstalle: string | null;
    tidsenhet: string | null;
    behandlingsandamal: string | null;
    dosering: string | null;
    tid_mellan_uttag_varde: number | null;
    behandlingsorsak_kod: string | null;
    precisering_administreringsstalle: string | null;
    medicinteknisk_produkt: string | null;
    administreringsvag_annan: string | null;
    administreringsstalle_annan: string | null;
    administreringsmetod_annan: string | null;
    dosering_annan_instruktion: string | null;
    medicinteknisk_produkt_annan: string | null;
    precisering_administreringsstalle_annan: string | null;
    akut_forandring: string | null;
    bytes_ej: string | null;
    sparrar?: Array<Sparr>;
    handelser?: Array<Handelse>;
    uttag?: ReceptforskrivningUttag[];
    beskrivning_handelseorsak: string | null;
    handelseorsak: string | null;
};

export type ReceptforskrivningLakemedel = ReceptforskrivningBase & {
    handelsvara?: never;
    lakemedel: Lakemedel;
};

export type ReceptforskrivningHandelsvara = ReceptforskrivningBase & {
    handelsvara: Handelsvara;
    lakemedel?: never;
};

export type Receptforskrivning = ReceptforskrivningHandelsvara | ReceptforskrivningLakemedel;

export type ReceptforskrivningDisplay = Receptforskrivning & {
    ikoner?: {
        [key: string]: boolean | number;
        narkotikaNiva: number;
    };
    styrka?: LakemedelDisplay["styrka"];
    substans?: LakemedelDisplay["substans"];
    atc?: LakemedelDisplay["atc"];
    form?: LakemedelDisplay["form"];
    lakemedelInfo: LakemedelDisplay["lakemedel"] | HandelsvaraDisplay["lakemedelInfo"];
    uttag_kvar: string;
    forpackningsinnehall?: LakemedelDisplay["forpackning"];
    linkFass?: LakemedelDisplay["linkFass"];
    parallell?: string;
    kedja?: boolean;
    dosdispensering?: boolean;
    uttag: Array<ReceptforskrivningUttag & { lakemedelInfo: LakemedelDisplay["lakemedel"] }>;
    mangd?: number;
    patient_k3_hade_personnummer?: boolean;
    uttag_pappersrecept?: boolean;
    apotek?: {
        label: string;
        value: string;
        adress: string;
    };
    lakemedelId?: LakemedelDisplay["id"];
    nplId?: LakemedelDisplay["nplId"];
    isHandelsvara?: boolean;
    annan_instruktion?: string;
    vid_behov: boolean;
    forskrivningsstatusorsaksbeskrivning?: string;
    utfardandedatumDate: string;
};
